import React, { useState, useEffect } from "react"

import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorToggle from "../shared/BackgroundColorToggle"

const ReviewsEditor = ({ section, practice, onSave, onCancel, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()
  const [formData, setFormData] = useState({
    headline: section?.headline || "Client Reviews",
    subheadline: section?.subheadline || "See what our clients are saying",
    body: section?.body || "",
    imageUrl: section?.imageUrl || practice?.reviewsSectionImageUrl || "",
    settings: section?.settings || {
      displayCount: 3,
      showRatings: true,
      showPhotos: true,
      layout: "cards",
      selectedReviewIds: [],
      showVideoReviews: true,
      videoReviewsCount: 3,
      selectedVideoReviewIds: [],
      showTextReviews: true,
      textReviewsCount: 3,
      showDates: true,
      showOverallRating: true,
      showCTA: true,
      ctaText: "View all reviews",
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      showTextColor: false,
      textColor: "#55433f"
    }
  })

  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "Client Reviews",
        subheadline: section.subheadline || "See what our clients are saying",
        body: section.body || "",
        imageUrl: section.imageUrl || practice?.reviewsSectionImageUrl || "",
        settings: {
          displayCount: 3,
          showRatings: true,
          showPhotos: true,
          layout: "cards",
          selectedReviewIds: [],
          showVideoReviews: true,
          videoReviewsCount: 3,
          selectedVideoReviewIds: [],
          showTextReviews: true,
          textReviewsCount: 3,
          showDates: true,
          showOverallRating: true,
          showCTA: true,
          ctaText: "View all reviews",
          ...section.settings
        }
      })
    }
  }, [section, practice])

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }

    // Update preview immediately for content changes
    if (section?.id && ["headline", "subheadline", "body", "imageUrl"].includes(field)) {
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        [field]: value
      })
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const newSettings = { ...prev.settings, [key]: value }

      // Immediately update preview for important settings (avoids useEffect loops)
      const importantSettings = [
        "displayCount",
        "showRatings",
        "showPhotos",
        "layout",
        "showVideoReviews",
        "videoReviewsCount",
        "selectedVideoReviewIds",
        "showTextReviews",
        "textReviewsCount",
        "showDates",
        "showOverallRating",
        "showCTA",
        "ctaText",
        "showBackgroundColor",
        "backgroundColor",
        "showTextColor",
        "textColor"
      ]
      if (section?.id && importantSettings.includes(key)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...prev,
          settings: newSettings
        })
      }

      return {
        ...prev,
        settings: newSettings
      }
    })
  }

  const handleReviewSelection = (reviewId) => {
    const currentSelected = formData.settings.selectedReviewIds || []
    const newSelected = currentSelected.includes(reviewId)
      ? currentSelected.filter((id) => id !== reviewId)
      : [...currentSelected, reviewId]

    handleSettingsChange("selectedReviewIds", newSelected)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.headline.trim()) {
      newErrors.headline = "Section headline is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validateForm()) {
      onSave({
        layout: "reviews",
        ...formData
      })
    }
  }

  // Mock reviews data (in real app, this would come from practice.reviews)
  const availableReviews = practice?.reviews || [
    {
      id: 1,
      review: "Amazing experience! The service was professional and exceeded my expectations. Highly recommend!",
      rating: 5,
      client: { name: "Sarah J." },
      createdAt: "2024-01-15"
    },
    {
      id: 2,
      review: "Great results and very knowledgeable staff. Will definitely be coming back for future appointments.",
      rating: 5,
      client: { name: "Michael R." },
      createdAt: "2024-01-20"
    },
    {
      id: 3,
      review:
        "Professional service in a comfortable environment. The team made me feel at ease throughout the process.",
      rating: 4,
      client: { name: "Jennifer L." },
      createdAt: "2024-01-25"
    }
  ]

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`text-sm ${i < rating ? "text-yellow-400" : "text-gray-300"}`}>
        ★
      </span>
    ))

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {/* Section Headline */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Section headline</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Create a compelling headline for your reviews section.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.headline}
                onChange={(e) => handleChange("headline", e.target.value)}
                placeholder="Client Reviews"
                className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                required
              />
            </div>
            {errors.headline && (
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
                <p className="block leading-[19.5px]">{errors.headline}</p>
              </div>
            )}
          </div>
        </div>

        {/* Subheadline */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Subheadline</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Add supporting text for your reviews section.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.subheadline}
                onChange={(e) => handleChange("subheadline", e.target.value)}
                placeholder="Supporting text for your reviews section"
                className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Description</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Add additional context about your reviews.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative h-20 w-full shrink-0 rounded-lg bg-white">
              <textarea
                value={formData.body}
                onChange={(e) => handleChange("body", e.target.value)}
                placeholder="Additional context about your reviews..."
                rows={3}
                className="box-border flex h-20 w-full resize-none flex-row content-stretch items-start justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Review Selection */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Select reviews to feature</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Choose which reviews to display in this section.</p>
            </div>
          </div>

          {availableReviews.length > 0 ? (
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-3 p-0">
              {availableReviews.map((review) => (
                <div
                  key={review.id}
                  className={`relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start overflow-hidden text-clip rounded-lg border p-4 transition-colors ${
                    (formData.settings.selectedReviewIds || []).includes(review.id)
                      ? "border-[#0d9da4] bg-[#f5fafd]"
                      : "border-[#c8d6df] hover:border-[#0d9da4]"
                  }`}>
                  {/* Checkbox at the top */}
                  <div className="relative mb-3 box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between p-0">
                    <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                      <p className="block whitespace-pre leading-[20px]">Select this review</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={(formData.settings.selectedReviewIds || []).includes(review.id)}
                      onChange={() => handleReviewSelection(review.id)}
                      className="relative size-5 shrink-0 accent-[#0d9da4]"
                    />
                  </div>

                  {/* Review content */}
                  <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-start justify-start gap-4 p-0">
                    <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                      <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-2 p-0">
                        <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                          <p className="block whitespace-pre leading-[24px]">{review.client?.name || "Anonymous"}</p>
                        </div>
                        {formData.settings.showRatings && (
                          <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-0.5 p-0">
                            {renderStars(review.rating)}
                          </div>
                        )}
                      </div>
                      <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-2 overflow-hidden p-0">
                        <div className="relative shrink-0 overflow-hidden text-left font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-[#0b3954]">
                          <p className="block break-words leading-[24px]">&quot;{review.review}&quot;</p>
                        </div>
                        <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                          <p className="block leading-[19.5px]">{new Date(review.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-center justify-center gap-2 text-clip rounded-lg border-2 border-dashed border-[#c8d6df] bg-white py-8">
              <div className="relative shrink-0 text-center font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-[#55778a]">
                <p className="block leading-[24px]">No reviews found</p>
              </div>
              <div className="relative shrink-0 text-center font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                <p className="block leading-[19.5px]">Client reviews will appear here once you receive them</p>
              </div>
            </div>
          )}

          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-1 text-clip rounded-lg bg-[#f5fafd] p-3">
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                <p className="block leading-[19.5px]">
                  <span className="font-['Manrope:Bold',_sans-serif] font-bold">Selected:</span>{" "}
                  {(formData.settings.selectedReviewIds || []).length} review
                  {(formData.settings.selectedReviewIds || []).length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Display Settings */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Review display settings</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Configure how reviews are displayed.</p>
            </div>
          </div>

          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-start justify-start gap-4 p-0">
              <div className="relative box-border flex shrink-0 grow basis-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[20px]">Layout style</p>
                </div>
                <div className="relative w-full shrink-0 rounded-lg bg-white">
                  <select
                    value={formData.settings.layout || "cards"}
                    onChange={(e) => handleSettingsChange("layout", e.target.value)}
                    className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none">
                    <option value="cards">Card Layout</option>
                    <option value="reviews">Testimonial Style</option>
                    <option value="list">Simple List</option>
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                    <svg
                      className="h-4 w-4 text-[#55778a] opacity-65"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="relative box-border flex shrink-0 grow basis-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[20px]">Reviews to display</p>
                </div>
                <div className="relative w-full shrink-0 rounded-lg bg-white">
                  <select
                    value={formData.settings.displayCount || 3}
                    onChange={(e) => handleSettingsChange("displayCount", parseInt(e.target.value))}
                    className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none">
                    <option value={3}>3 Reviews</option>
                    <option value={5}>5 Reviews</option>
                    <option value={10}>10 Reviews</option>
                    <option value={-1}>All Selected Reviews</option>
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                    <svg
                      className="h-4 w-4 text-[#55778a] opacity-65"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-3 p-0">
              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between p-0">
                <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0">
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                    <p className="block whitespace-pre leading-[24px]">Show star ratings</p>
                  </div>
                  <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-[#55778a]">
                    <p className="block leading-[20px]">Display star ratings with reviews</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.settings.showRatings !== false}
                  onChange={(e) => handleSettingsChange("showRatings", e.target.checked)}
                  className="relative size-5 shrink-0 accent-[#0d9da4]"
                />
              </div>

              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between p-0">
                <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0">
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                    <p className="block whitespace-pre leading-[24px]">Show review dates</p>
                  </div>
                  <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-[#55778a]">
                    <p className="block leading-[20px]">Display when reviews were written</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.settings.showDates !== false}
                  onChange={(e) => handleSettingsChange("showDates", e.target.checked)}
                  className="relative size-5 shrink-0 accent-[#0d9da4]"
                />
              </div>

              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between p-0">
                <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0">
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                    <p className="block whitespace-pre leading-[24px]">Auto-rotate</p>
                  </div>
                  <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-[#55778a]">
                    <p className="block leading-[20px]">Automatically cycle through reviews</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.settings.autoRotate || false}
                  onChange={(e) => handleSettingsChange("autoRotate", e.target.checked)}
                  className="relative size-5 shrink-0 accent-[#0d9da4]"
                />
              </div>
            </div>

            {/* Overall Rating Display */}
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-3 text-clip rounded-lg bg-[#f5fafd] p-4">
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                <p className="block whitespace-pre leading-[24px]">Overall rating summary</p>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-0.5 p-0">
                  {renderStars(practice?.reviewsAverage || 5)}
                </div>
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                  <p className="block whitespace-pre leading-[24px]">{practice?.reviewsAverage?.toFixed(1) || "5.0"}</p>
                </div>
                <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-[#55778a]">
                  <p className="block leading-[20px]">({practice?.reviewsCount || 0} reviews)</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-2 p-0">
                <input
                  type="checkbox"
                  id="showOverallRating"
                  checked={formData.settings.showOverallRating !== false}
                  onChange={(e) => handleSettingsChange("showOverallRating", e.target.checked)}
                  className="relative size-4 shrink-0 accent-[#0d9da4]"
                />
                <label
                  htmlFor="showOverallRating"
                  className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-[#0b3954]">
                  <p className="block leading-[20px]">Show overall rating summary above reviews</p>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Background Color Section */}
        <BackgroundColorToggle formData={formData} onSettingsChange={handleSettingsChange} sectionType="reviews" />

        {/* Section Text Color Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Section text color</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Customize text color for better contrast with background.</p>
            </div>
          </div>
          <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
            <input
              type="checkbox"
              checked={formData.settings.showTextColor || false}
              onChange={(e) => handleSettingsChange("showTextColor", e.target.checked)}
              className="relative size-5 shrink-0 accent-[#0d9da4]"
            />
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-[#0b3954]">
              <p className="block whitespace-pre leading-[24px]">Text color</p>
            </div>
          </div>

          {formData.settings.showTextColor && (
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-3 p-0">
              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                    <p className="block whitespace-pre leading-[20px]">Text color</p>
                  </div>
                </div>
                <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-2 p-0">
                  <input
                    type="color"
                    value={formData.settings.textColor || "#55433f"}
                    onChange={(e) => handleSettingsChange("textColor", e.target.value)}
                    className="relative h-8 w-12 shrink-0 cursor-pointer rounded border border-[#c8d6df]"
                  />
                  <input
                    type="text"
                    value={formData.settings.textColor || "#55433f"}
                    onChange={(e) => handleSettingsChange("textColor", e.target.value)}
                    placeholder="#55433f"
                    className="relative box-border flex w-24 flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-2 py-1 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[20px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="box-border flex w-full flex-row content-stretch items-center justify-between border-t border-[#e4eff6] bg-white p-[32px]">
        <div className="relative box-border flex min-h-px min-w-px shrink-0 grow basis-0 flex-row content-stretch items-center justify-end gap-4 p-0">
          <button
            onClick={() => {
              clearPreview() // Clear any preview changes
              onCancel()
            }}
            className="relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 overflow-visible rounded border border-[#c8d6df] bg-white px-4 py-0 transition-colors hover:bg-gray-50">
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
              <p className="block whitespace-pre leading-[32px]">Close</p>
            </div>
          </button>
          <button
            onClick={handleSubmit}
            className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-center gap-2 rounded bg-[#0d9da4] px-4 py-0 transition-colors hover:bg-[#0b8a90]">
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-white">
              <p className="block whitespace-pre leading-[32px]">{isEditing ? "Update Reviews" : "Add Reviews"}</p>
            </div>
          </button>
        </div>
      </div>
    </>
  )
}

export default ReviewsEditor
